/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 * Page News Archive component
 *
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.25.0
 */

import { FC } from 'react';

import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { PageTitle } from '@/main/components/common';
import Title from '@/main/components/common/Title/Title';
import PageLocale from '@/main/modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@/main/modules/internationalization/constants';
import {
    makeSelectPageNewsTranslation,
    makeSelectTranslationCommon,
} from '@/main/modules/internationalization/intSelectors';
import { ECommonTranslationPages } from '@/main/modules/internationalization/types/common/pages';
import { KitLoader } from '@/main/modules/kitLoader';
import { IRouteParams } from '@/main/routes/constants/routesConstants';
import { icons as themeIcons } from '@theme/index';

import ErrorPage from '../ErrorPage/ErrorPage';
import NewsList from './components/NewsList';
import NewsView from './components/NewsView';
import { useGetNewsItemQuery, useGetNewsListQuery } from './store';
import { PageNewsContainerStyled, PageNewsWrapperStyled } from './styled/PageNewsStyled';

const PageNews: FC = () => {
    const { userNews: translation } = useSelector(makeSelectPageNewsTranslation());
    const tCommon = useSelector(makeSelectTranslationCommon());

    const { data: newsListData, isError: isNewsListError, isFetching: isNewsListFetching } = useGetNewsListQuery();

    const { newsId } = useParams<IRouteParams>();

    const { data: newsItemData, isFetching: isNewsItemLoading } = useGetNewsItemQuery(
        { id: Number(newsId) },
        {
            skip: !newsId,
        },
    );

    if (isNewsListError)
        return (
            <ErrorPage
                src={themeIcons.errorDeliveryImage3x}
                text={tCommon.error.ui.text}
                title={tCommon.error.ui.title}
            />
        );

    return (
        <PageNewsContainerStyled>
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.pageNews]} />
            <Title pageName={ECommonTranslationPages.news} />
            <PageTitle title={translation.header.title} />
            <KitLoader showLoader={isNewsListFetching}>
                <PageNewsWrapperStyled>
                    <NewsList news={newsListData} />
                    <NewsView isLoading={isNewsItemLoading} item={newsItemData} />
                </PageNewsWrapperStyled>
            </KitLoader>
        </PageNewsContainerStyled>
    );
};

export default PageNews;
